/** `%PDF`. */
const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46];
/** `PK\x03\x04` — a .docx is an OOXML package, which is always a Zip. */
const ZIP_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];

const SIGNATURE_BYTES = 4;

export type SniffedUploadFormat = 'pdf' | 'zip' | 'other';

const startsWith = (bytes: Uint8Array, signature: number[]): boolean =>
	bytes.length >= signature.length && signature.every((byte, index) => bytes[index] === byte);

/** `FileReader` rather than `Blob.arrayBuffer`, which jsdom 26 does not implement. */
const readSignatureBytes = (file: File): Promise<Uint8Array> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () =>
			resolve(
				reader.result instanceof ArrayBuffer
					? new Uint8Array(reader.result)
					: new Uint8Array()
			);
		reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
		reader.readAsArrayBuffer(file.slice(0, SIGNATURE_BYTES));
	});

/**
 * The file's real format from its first bytes.
 *
 * `File.type` is derived from the EXTENSION by every desktop browser, so a PDF renamed `.docx`
 * claims the Word MIME type and passes any check made on that. It then uploads, sniffs as a
 * PDF on the backend, takes the PDF path — and every copy an investor downloads is a PDF
 * called `.docx` that Word refuses to open.
 *
 * `zip` rather than `docx`: four bytes prove a Zip, not that it holds a `word/document.xml`.
 * That distinction is the backend's, which has the whole package to read.
 */
export const sniffUploadFormat = async (file: File): Promise<SniffedUploadFormat> => {
	let header: Uint8Array;
	try {
		header = await readSignatureBytes(file);
	} catch {
		// Unreadable here is no evidence of a rename, and the backend reads the bytes anyway.
		return 'other';
	}
	if (startsWith(header, PDF_SIGNATURE)) {
		return 'pdf';
	}
	if (startsWith(header, ZIP_SIGNATURE)) {
		return 'zip';
	}
	return 'other';
};

/**
 * Which of the two supported formats the NAME promises, or null for anything else.
 *
 * Read from the name rather than `file.type` precisely because the name is what a rename
 * changes, and the name is what the download is served as.
 */
export const promisedUploadFormat = (fileName: string): 'pdf' | 'docx' | null => {
	const lowered = fileName.toLowerCase();
	if (lowered.endsWith('.pdf')) {
		return 'pdf';
	}
	if (lowered.endsWith('.docx')) {
		return 'docx';
	}
	return null;
};

/**
 * Which of the two named-vs-real contradictions this file is, or null.
 *
 * Only a POSITIVE contradiction — the bytes are definitely the OTHER supported format — is
 * reported. Bytes that are neither are left to the backend, whose detector can tell a legacy
 * `.doc` from junk and says which it is.
 */
export const renamedUploadKind = (
	promised: 'pdf' | 'docx' | null,
	sniffed: SniffedUploadFormat
): 'pdf_named_docx' | 'docx_named_pdf' | null => {
	if (promised === 'docx' && sniffed === 'pdf') {
		return 'pdf_named_docx';
	}
	if (promised === 'pdf' && sniffed === 'zip') {
		return 'docx_named_pdf';
	}
	return null;
};
